import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseHorizontalNavigationComponent, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FooterType01Component } from 'app/layout/common/footer/footer-type01/footer-type01.component';
import { FooterType02Component } from 'app/layout/common/footer/footer-type02/footer-type02.component';
import { FooterComponent } from 'app/layout/common/footer/footer.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { TFMService } from 'app/mock-api/tfm.service';
import { TFMCartService } from 'app/mock-api/tfm-cart.service';
import { Subject, takeUntil } from 'rxjs';
import { CartComponent } from 'app/layout/common/cart/cart.component';
import { FooterType03Component } from "../../../common/footer/footer-type03/footer-type03.component";
import { FooterType04Component } from "../../../common/footer/footer-type04/footer-type04.component";
import { FooterType05Component } from "../../../common/footer/footer-type05/footer-type05.component";
import { FooterType06Component } from "app/layout/common/footer/footer-type06/footer-type06.component";
import { FooterType07Component } from "app/layout/common/footer/footer-type07/footer-type07.component";

@Component({
    selector     : 'centered-layout',
    templateUrl  : './centered.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports: [FuseLoadingBarComponent, NgIf, FuseVerticalNavigationComponent, FuseHorizontalNavigationComponent, MatButtonModule, MatIconModule, CartComponent,
    LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, NotificationsComponent, UserComponent, RouterOutlet,
    FooterComponent, FooterType01Component, FooterType02Component, RouterLink, FooterType03Component, FooterType04Component, FooterType05Component, FooterType06Component, FooterType07Component],
})
export class CenteredLayoutComponent implements OnInit, OnDestroy
{
    public view : any = {
        isClient : false,
        data: {
          cart: []
        },
        drawers: {
          review:{}
        }
      };
      public drawerMode: 'over' | 'side' | 'push' = 'push';


    navigation: Navigation;
    isScreenSmall: boolean;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private tfmService: TFMService,
        private _tfmCartService: TFMCartService,
        private _cd: ChangeDetectorRef
    )
    {
         this.view.tripChange$ = this._tfmCartService.tripChange.subscribe(res => {
            this.view.trip = res;
        });

        this.view.cartChange$ = this._tfmCartService.cartChange.subscribe(res => {
            console.log("cart", res);

            this.view.data.cart = res;

            this._cd.detectChanges();
        });
    }

    /**
     * Whether the current route is the search result screen (cart should show limited columns).
     */
    isSearchResultRoute(): boolean {
        return this._router.url.includes('search-results');
    }

    /** True only for Optima Vibe (site id 74, code OVT). Used so "Optima Rail" and "Our Blog" nav items never show for other sites (e.g. XL Elite/ET). */
    get isOptimaVibeSite(): boolean {
        const site = this.view?.site;
        if (!site) return false;
        return site.id === 74 && (site.code || '').toUpperCase() === 'OVT';
    }

    /** XL Elite Travel (88) and XL International Travels (96): same compact logo, centered nav, site-88-nav styles. */
    get isEliteMemberLayoutSite(): boolean {
        const id = this.view?.site?.id;
        return id === 88 || id === 96;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.view.isClient = this.tfmService.view?.isClient ?? false;

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) =>
                {
                    if (this.view.isClient) {
                        this.tfmService.getDestinations().then((res:any) => {
                            const grouped: Record<string, any[]> = {};
                            res.forEach(x => {
                                const continent = x.parent?.name || 'Other';
                                if (!grouped[continent]) grouped[continent] = [];
                                grouped[continent].push({
                                    id: x.id, title: x.name, type: "basic", icon: "", link: `/destination/${x.id}/${x.seo.alias}`
                                });
                            });
                            const destinations = Object.keys(grouped).sort().map(continent => ({
                                id: continent.toLowerCase().replace(/\s+/g, '-'),
                                title: continent,
                                subtitle: "",
                                type: "collapsable" as const,
                                icon: "",
                                children: grouped[continent]
                            }));

                            this.view.pages = res;
                        
                            navigation.horizontal = [];
                            
                            if (this.view.site && this.view.site.profile && this.view.site.profile.heading) {
                                switch (this.view.site.profile.heading.menu) {
                                    case 1: {
                                        navigation.horizontal.push(
                                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                            { id: "destinations", title: "Destinations", subtitle: "", type: "group", icon: "feather:chevron-down", children: destinations },
                                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                                        );
                                        break;
                                    }
                                    case 2: {
                                        navigation.horizontal.push(
                                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "about-us" },
                                            { id: "members", title: "OUR MEMBERS", subtitle: "", type: "basic", icon: "", children: [], link: "/branches" },
                                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                                            { id: "leisure-travel", title: "HOLIDAYS", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel" },
                                            // { id: "branches", title: "Members", subtitle: "", type: "basic", icon: "", children: [], link: "branches" },
                                            { id: "promotional", title: "SPECIALS", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                                            { id: "travel-app", title: "XLGO TRAVEL APP", subtitle: "", type: "basic", icon: "", children: [], link: "https://www.xltravel.co.za/xlgo/", externalLink: true, target: "_blank" },
                                            // { id: "sports-packages", title: "Sports", subtitle: "", type: "basic", icon: "", children: [], link: "/packages/sports-packages" },
                                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "contact-us" },
                                        );
                                        break;
                                    }
                                    case 3: {
                                        navigation.horizontal.push(
                                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                            { id: "world-cruises", title: "World Cruises", subtitle: "", type: "basic", icon: "", children: [], link: "/world-cruises" },
                                            { id: "gallery", title: "Gallery", subtitle: "", type: "basic", icon: "", children: [], link: "/gallery" },
                                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact" },
                                        );
                                        break;
                                    }
                                    case 4: {
                                        navigation.horizontal.push(
                                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                                            ...(this.isOptimaVibeSite ? [{ id: "rail", title: "Optima Rail", subtitle: "", type: "basic" as const, icon: "", children: [], link: "/packages/rail" }] : []),
                                            { id: "corporate-travel", title: "Business Travel", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                                            { id: "leisure-travel", title: "Holiday Travel", subtitle: "", type: "basic", icon: "", children: [], link: "packages" },
                                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                                            ...(this.isOptimaVibeSite ? [{ id: "blog", title: "Our Blog", subtitle: "", type: "basic" as const, icon: "", children: [], link: "/blog/travel-blog" }] : []),
                                            // { id: "cic-booking-form", title: "CIC Bookings", subtitle: "", type: "basic", icon: "", children: [], link: "/cic-booking-form" },
                                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                                        );
                                        break;
                                    }
                                    case 5: {
                                        navigation.horizontal.push(
                                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                            // { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                                            // { id: "members", title: "OUR MEMBERS", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                                            { id: "leisure-travel", title: "Leisure Travel", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel" },
                                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                                            // { id: "travel-app", title: "XL GO TRAVEL APP", subtitle: "", type: "basic", icon: "", children: [], link: "https://www.xltravel.co.za/xlgo/", externalLink: true, target: "_blank" },
                                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                                        );
                                        break;
                                    }
                                    case 6: {
                                        navigation.horizontal.push(
                                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                                            { id: "non-profit", title: "Humanitarian", subtitle: "", type: "basic", icon: "", children: [], link: "/non-profit" },
                                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                                            { id: "leisure-travel", title: "Leisure Travel", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel" },
                                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact" },
                                        );
                                        break;
                                    }
                                    case 7 : {
                                        navigation.horizontal.push(
                                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                                            { id: "leisure-travel", title: "Packages", subtitle: "", type: "basic", icon: "", children: [], link: "packages" },
                                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                                        );
                                        break;
                                    }
                                    default: {
                                        //console.log('No matching case found for services');
                                    }
                                }
                            }
                            navigation.horizontal = navigation.horizontal.filter(
                                (item: any) => item.id !== 'blog' || this.isOptimaVibeSite
                            );
                            this.navigation = navigation;
                            this._cd.detectChanges();
                            // console.log("This is a Navigation data :",this.navigation);
                        });
                    }
                    else {
                        this.navigation = navigation;
                    }
                });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
        this.loadSiteForCenteredLayout();

        this.tfmService.localAgencySite$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                if (this.view.isClient) {
                    this.loadSiteForCenteredLayout();
                }
            });

        this.view.itinGuidChange$ = this.tfmService.itinGuidChange.subscribe(res => {
            this.view.itinGuid = this.tfmService.getLocalItinGuid();
      
            //this._changeDetectorRef.detectChanges();
          });
      
          this.view.itinGuid = this.tfmService.getLocalItinGuid();
    }

    /**
     * Load site + nav after localStorage matches the selected channel (?ai=&sc= may apply after first paint).
     */
    private loadSiteForCenteredLayout(): void {
        this.tfmService.getSite().then(res => {
            this.view.site = res;
            this.view.site.profile = this.tfmService.getSiteProfile(this.view.site.profileID);

            // Rebuild horizontal nav when site is loaded so menu case (e.g. case 5) and Contact link are correct
            if (this.view.isClient && this.navigation && this.view.site?.profile?.heading) {
                this.tfmService.getDestinations().then((destinationsRes: any) => {
                    const grouped: Record<string, any[]> = {};
                    (destinationsRes || []).forEach((x: any) => {
                        const continent = x.parent?.name || 'Other';
                        if (!grouped[continent]) grouped[continent] = [];
                        grouped[continent].push({
                            id: x.id, title: x.name, type: "basic", icon: "", link: `/destination/${x.id}/${x.seo.alias}`
                        });
                    });
                    const destinations = Object.keys(grouped).sort().map(continent => ({
                        id: continent.toLowerCase().replace(/\s+/g, '-'),
                        title: continent,
                        subtitle: "",
                        type: "collapsable" as const,
                        icon: "",
                        children: grouped[continent]
                    }));
                    this.navigation.horizontal = [];
                    const menu = this.view.site.profile.heading.menu;
                    if (menu === 1) {
                        this.navigation.horizontal.push(
                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                            { id: "destinations", title: "Destinations", subtitle: "", type: "group", icon: "feather:chevron-down", children: destinations },
                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                        );
                    } else if (menu === 2) {
                        this.navigation.horizontal.push(
                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "about-us" },
                            { id: "members", title: "OUR MEMBERS", subtitle: "", type: "basic", icon: "", children: [], link: "/branches" },
                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                            { id: "leisure-travel", title: "HOLIDAYS", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel" },
                            { id: "promotional", title: "SPECIALS", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                            { id: "travel-app", title: "XLGO TRAVEL APP", subtitle: "", type: "basic", icon: "", children: [], link: "https://www.xltravel.co.za/xlgo/", externalLink: true, target: "_blank" },
                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                        );
                    } else if (menu === 3) {
                        this.navigation.horizontal.push(
                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                            { id: "world-cruises", title: "World Cruises", subtitle: "", type: "basic", icon: "", children: [], link: "/world-cruises" },
                            { id: "gallery", title: "Gallery", subtitle: "", type: "basic", icon: "", children: [], link: "/gallery" },
                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                        );
                    } else if (menu === 4) {
                        this.navigation.horizontal.push(
                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                            { id: "corporate-travel", title: "Business Travel", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                            { id: "leisure-travel", title: "Holiday Travel", subtitle: "", type: "basic", icon: "", children: [], link: "packages" },
                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
...(this.isOptimaVibeSite ? [{ id: "blog", title: "Our Blog", subtitle: "", type: "basic" as const, icon: "", children: [], link: "/blog/travel-blog" }] : []),
                                            { id: "cic-booking-form", title: "CIC Bookings", subtitle: "", type: "basic", icon: "", children: [], link: "/cic-booking-form" },
                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                        );
                    } else if (menu === 5) {
                        this.navigation.horizontal.push(
                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                            { id: "members", title: "OUR MEMBERS", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                            { id: "leisure-travel", title: "Leisure Travel", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel" },
                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                            { id: "travel-app", title: "XL GO TRAVEL APP", subtitle: "", type: "basic", icon: "", children: [], link: "https://www.xltravel.co.za/xlgo/", externalLink: true, target: "_blank" },
                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                        );
                    } else if (menu === 6) {
                        this.navigation.horizontal.push(
                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                            { id: "non-profit", title: "Humanitarian", subtitle: "", type: "basic", icon: "", children: [], link: "/non-profit" },
                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                            { id: "leisure-travel", title: "Leisure Travel", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel" },
                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                        );
                    } else if (menu === 7) {
                        this.navigation.horizontal.push(
                            { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                            { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                            { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                            { id: "corporate-travel", title: "Corporate", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                            { id: "leisure-travel", title: "Packages", subtitle: "", type: "basic", icon: "", children: [], link: "packages" },
                            { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                            { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                        );
                    }
                    this.navigation.horizontal = this.navigation.horizontal.filter(
                        (item: any) => item.id !== 'blog' || this.isOptimaVibeSite
                    );
                    this._cd.detectChanges();
                });
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }
        toggleNavigationB2C(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }
    viewChanged(options) {
        console.log('options',options)
      this.view.drawerOptions = options;
    }
    quoteCreated(quoteGuid) {
        console.log("quote", quoteGuid);
        if (quoteGuid)
            this._router.navigate(['/checkout', quoteGuid]);
    }
}
